<script lang="ts">
  import ActionButton from '../ActionButton.svelte';
  import Refresh from '~icons/mdi/refresh';
  import Premium from '~/icons/Premium.svelte';
  import {
    stringifyPuzzleKey,
    isPremium,
    puzzleQuery,
  } from '~/lib/queries.svelte';
  import { selectedDate } from '../../calendar/Calendar.svelte';
  import Countdown from '~/icons/Countdown.svelte';
  import { derived as _derived } from 'svelte/store';
  import { goto } from '$app/navigation';
  import { page } from '$app/state';
  import { DailyGame } from '~/store/daily.svelte';
  import { getGame, setGame } from '~/store/GameContext.svelte';
  import { PersistedState } from 'runed';
  import Spinner from '~/components/Spinner.svelte';

  type Props = {
    iconOnly?: boolean;
    disabled?: boolean;
  };
  const props: Props = $props();
  const premium = isPremium();
  const game = $derived(getGame<DailyGame>().current);
  const set = setGame();
  // TODO not hitting cache on replay, could it be that the variant isn't set correctly?
  const puzzle = $derived(puzzleQuery(stringifyPuzzleKey(game)));

  const replaysLeft = new PersistedState('replays-left', 5);
  const canReplay = $derived(replaysLeft.current > 0 || premium.data);
  // wait for the premium check and puzzle to settle so the click never acts
  // on a stale premium status or a missing puzzle
  const pending = $derived(
    (!!page.data.user && premium.isFetching) || !puzzle.data,
  );

  const handleReplay = async () => {
    const data = puzzle.data;
    if (pending || !data) return;
    if (canReplay) {
      replaysLeft.current--;
      selectedDate.current = data.date;
      const newGame = await new DailyGame({
        mode: 'daily', // TODO do I need this?
        variant: game.variant,
        id: data.id,
        key: data.key,
        date: data.date,
        completed: false,
        stars: 3,
        fullClear: false,
        board: data.board,
      }).init(game.variant);
      await newGame.reset();
      set(newGame);
    } else {
      await goto('?premium=t#archive', { state: page.state });
    }
  };
</script>

<ActionButton
  disabled={props.disabled || pending}
  iconOnly={props.iconOnly}
  class={[premium.data ? 'premium' : 'countdown', 'replay'].join(' ')}
  onClick={handleReplay}
>
  {#if pending}
    <span class="spinner"><Spinner /></span>
  {:else if premium.data}
    <Refresh color="#36AE7C" />
  {:else if canReplay}
    <Countdown color="#EB5353" n={Math.max(replaysLeft.current, 0)} />
  {:else}
    <Premium />
  {/if}
  Replay
</ActionButton>

<style lang="scss">
  @use '~/theme/theme';

  .replay {
    :global(svg) {
      transform: scale(1.5);
    }
    :global(.action.premium svg path) {
      fill: theme.$star-bonus;
    }
    :global(.action.countdown svg path) {
      fill: theme.$star-penalty;
    }
  }
  // shrink the 80px spinner to icon size so the button doesn't jump
  .spinner {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    :global(.ellipsis) {
      flex-shrink: 0;
      transform: scale(0.3);
    }
  }
</style>
